/**
 * PodcastTab.jsx — Thin orchestrator.
 * All state + API logic lives here; UI split into sub-components in ./podcast/.
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { TabsContent } from "../ui/tabs";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../config";
import { useAuth } from "../../AuthContext";
import { STATUS_META } from "./podcast/PodcastEpCard";
import { PodcastDetailPanel } from "./podcast/PodcastDetailPanel";
import { PodcastKanbanBoard } from "./podcast/PodcastKanbanBoard";
import { PodcastSettingsPanel } from "./podcast/PodcastSettingsPanel";
import { PodcastTtsLabPanel } from "./podcast/PodcastTtsLabPanel";

const POLL_INTERVAL = 4000;

const LANES = ["backlog","draft","approved","audio","published"];

export default function PodcastTab({ dm }) {
  const { authHeader } = useAuth();
  // Cookie auth: JWT rides in the HttpOnly cookie; we only need to attach the
  // CSRF token on write requests — authHeader() returns that.
  const headers = authHeader();

  // ── Theme classes ────────────────────────────────────────────────────────
  const c = dm
    ? { bg: "bg-[#1a2324]", card: "bg-[#252e2f]", text: "text-slate-200", muted: "text-slate-500", border: "border-slate-700/50", lane: "bg-[#1e2829]" }
    : { bg: "bg-[#f5f6f7]", card: "bg-white",     text: "text-[#191c1d]", muted: "text-[#6b7a7d]", border: "border-[#e7e8e9]",   lane: "bg-[#f0f1f2]" };

  const btnPrimary   = `flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all disabled:opacity-40 ${dm ? "bg-cyan-500 text-[#001f24] hover:bg-cyan-400" : "bg-[#006874] text-white hover:bg-[#004f5a]"}`;
  const btnSecondary = `flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-40 border ${dm ? "border-slate-600 text-slate-300 hover:bg-white/5" : "border-[#e7e8e9] text-[#3b494c] hover:bg-[#f5f6f7]"}`;
  const inputCls     = `w-full px-3 py-2 rounded-xl text-sm outline-none border transition-colors ${dm ? "bg-[#1a2324] border-slate-700 text-slate-200 placeholder:text-slate-600 focus:border-cyan-500" : "bg-[#f5f6f7] border-[#e7e8e9] text-[#191c1d] placeholder:text-[#bac9cc] focus:border-[#006874]"}`;
  const labelCls     = `block text-[10px] font-bold uppercase tracking-widest mb-1 ${dm ? "text-slate-500" : "text-[#bac9cc]"}`;

  // ── State ────────────────────────────────────────────────────────────────
  const [mainTab, setMainTab] = useState("kanban");
  const [episodes, setEpisodes] = useState([]);
  const [queue, setQueue] = useState([]);
  const [loadingEps, setLoadingEps] = useState(true);
  const [config, setConfig] = useState({});
  const [configForm, setConfigForm] = useState({});
  const [savingConfig, setSavingConfig] = useState(false);

  const [articleSearch, setArticleSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [runningSelection, setRunningSelection] = useState(false);
  const [queueOpen, setQueueOpen] = useState(false);

  const [generatingScript, setGeneratingScript] = useState(false);
  const [pollingScript, setPollingScript] = useState(false);
  const pollTimerRef = useRef(null);

  const [activeEpId, setActiveEpId] = useState(null);
  const [fullScript, setFullScript] = useState("");
  const [editedScript, setEditedScript] = useState("");
  const [editedTitle, setEditedTitle] = useState("");
  const [savingTitle, setSavingTitle] = useState(false);
  const [editedDescription, setEditedDescription] = useState("");
  const [savingDescription, setSavingDescription] = useState(false);
  const [generatingDesc, setGeneratingDesc] = useState(false);  const [loadingScript, setLoadingScript] = useState(false);
  const [refineInstruction, setRefineInstruction] = useState("");
  const [refining, setRefining] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [recordingEpId, setRecordingEpId] = useState(null);
  const [publishingEpId, setPublishingEpId] = useState(null);

  const [audioUrl, setAudioUrl] = useState(null);
  const [loadingAudio, setLoadingAudio] = useState(false);

  const [connectForm, setConnectForm] = useState({ client_id: "", client_secret: "" });
  const [connecting, setConnecting] = useState(false);
  const [uploadingStyleSheet, setUploadingStyleSheet] = useState(false);
  const [deletingEpId, setDeletingEpId] = useState(null);
  const [syncingSpReaker, setSyncingSpReaker] = useState(false);

  // Force-Include URL (backlog)
  const [forceUrl, setForceUrl] = useState("");
  const [scrapingForce, setScrapingForce] = useState(false);

  const [pushPermission, setPushPermission] = useState("default");
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushSubCount, setPushSubCount] = useState(0);

  // ── Data Loading ─────────────────────────────────────────────────────────
  const loadEpisodes = useCallback(async () => {
    setLoadingEps(true);
    try {
      const { data } = await axios.get(`${API}/admin/podcast/episodes`, { headers });
      setEpisodes(Array.isArray(data) ? data : (data.episodes || []));
    } catch { /* silent */ }
    finally { setLoadingEps(false); }
  }, []);

  const loadQueue = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/podcast/queue`, { headers });
      setQueue(Array.isArray(data) ? data : (data.queue || []));
    } catch { /* silent */ }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/podcast/config`, { headers });
      setConfig(data);
      setConfigForm(data);
    } catch { /* silent */ }
  }, []);

  const loadFullScript = useCallback(async (epId) => {
    if (!epId) return;
    setLoadingScript(true);
    try {
      const { data } = await axios.get(`${API}/admin/podcast/episodes/${epId}`, { headers });
      const sc = data.script || data.episode?.script || "";
      setFullScript(sc);
      setEditedScript(sc);
    } catch { toast.error("Could not load script"); }
    finally { setLoadingScript(false); }
  }, []);

  useEffect(() => {
    loadEpisodes();
    loadQueue();
    loadConfig();
  }, []);

  // Push notification init
  useEffect(() => {
    if (!("Notification" in window) || !("serviceWorker" in navigator)) return;
    setPushPermission(Notification.permission);
    navigator.serviceWorker.ready.then(async (reg) => {
      const sub = await reg.pushManager.getSubscription();
      setPushSubscribed(!!sub);
    }).catch(() => {});
    axios.get(`${API}/admin/push/status`, { headers })
      .then(({ data }) => setPushSubCount(data.active_subscriptions || 0))
      .catch(() => {});
  }, []);

  // Load script when activeEpId changes
  useEffect(() => {
    if (activeEpId) {
      setFullScript(""); setEditedScript(""); setAudioUrl(null);
      loadFullScript(activeEpId);
    }
  }, [activeEpId]);

  // Sync editedTitle when active episode changes
  useEffect(() => {
    const ep = episodes.find(e => e.id === activeEpId);
    setEditedTitle(ep?.title || "");
    setEditedDescription(ep?.description || "");
  }, [activeEpId, episodes]);

  // Cleanup polling on unmount
  useEffect(() => () => { if (pollTimerRef.current) clearInterval(pollTimerRef.current); }, []);

  // ── Polling helpers ───────────────────────────────────────────────────────

  /**
   * Poll podcast_config.last_gen_status until it leaves "running".
   * Used by generateScript() to track async script generation.
   */
  const startScriptPolling = useCallback(() => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    setPollingScript(true);
    pollTimerRef.current = setInterval(async () => {
      try {
        const { data: cfg } = await axios.get(`${API}/admin/podcast/config`, { headers });
        const genStatus = cfg.last_gen_status;
        if (genStatus && genStatus !== "running") {
          clearInterval(pollTimerRef.current);
          setGeneratingScript(false);
          setPollingScript(false);
          // Reload episodes to show newly created draft
          const { data } = await axios.get(`${API}/admin/podcast/episodes`, { headers });
          const eps = Array.isArray(data) ? data : (data.episodes || []);
          setEpisodes(eps);
          if (genStatus === "done") {
            const newEp = eps.find(e => ["draft","pending_approval"].includes(e.status));
            if (newEp) { toast.success("Script ready! Click to review."); setActiveEpId(newEp.id); }
            else toast.success("Script generated.");
          } else {
            toast.error("Script generation failed — check logs");
          }
        }
      } catch { /* silent */ }
    }, POLL_INTERVAL);
  }, []);

  /**
   * Poll episodes list until none of the watchStatuses remain.
   * Used for audio generation and Spreaker publishing.
   */
  const startEpisodePolling = useCallback((watchStatuses, onDone) => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    setPollingScript(true);
    pollTimerRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/admin/podcast/episodes`, { headers });
        const eps = Array.isArray(data) ? data : (data.episodes || []);
        setEpisodes(eps);
        const stillRunning = eps.some(e => watchStatuses.includes(e.status));
        if (!stillRunning) {
          clearInterval(pollTimerRef.current);
          setPollingScript(false);
          if (onDone) onDone(eps);
        }
      } catch { /* silent */ }
    }, POLL_INTERVAL);
  }, []);

  // ── Queue / Backlog ───────────────────────────────────────────────────────
  const autoSelect = async () => {
    setRunningSelection(true);
    try {
      const { data } = await axios.post(`${API}/admin/podcast/run-selection`, {}, { headers });
      // Show contextual message based on API response
      const msg = data.message || "Auto-selection done";
      toast.success(msg);
      await loadQueue();
    } catch { toast.error("Auto-selection failed"); }
    finally { setRunningSelection(false); }
  };

  const addToQueue = async (article) => {
    try {
      await axios.post(`${API}/admin/podcast/queue/${article.id}`, {}, { headers });
      toast.success("Added to backlog");
      await loadQueue();
    } catch { toast.error("Already in backlog or failed"); }
  };

  const saveTitle = async (epId, newTitle) => {
    const trimmed = newTitle.trim();
    if (!trimmed || trimmed === episodes.find(e => e.id === epId)?.title) return;
    setSavingTitle(true);
    try {
      await axios.patch(`${API}/admin/podcast/episodes/${epId}`, { title: trimmed }, { headers });
      setEpisodes(prev => prev.map(e => e.id === epId ? { ...e, title: trimmed } : e));
    } catch { toast.error("Could not save title"); }
    finally { setSavingTitle(false); }
  };

  const saveDescription = async (epId, newDesc) => {
    const trimmed = newDesc.trim();
    if (trimmed === (episodes.find(e => e.id === epId)?.description || "")) return;
    setSavingDescription(true);
    try {
      await axios.patch(`${API}/admin/podcast/episodes/${epId}`, { description: trimmed }, { headers });
      setEpisodes(prev => prev.map(e => e.id === epId ? { ...e, description: trimmed } : e));
    } catch { toast.error("Could not save description"); }
    finally { setSavingDescription(false); }
  };

  const generateDescription = async (epId) => {
    setGeneratingDesc(true);
    try {
      const res = await axios.post(`${API}/admin/podcast/episodes/${epId}/generate-description`, {}, { headers });
      const desc = res.data.description;
      setEditedDescription(desc);
      setEpisodes(prev => prev.map(e => e.id === epId ? { ...e, description: desc } : e));
      toast.success("Description generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate description");
    } finally { setGeneratingDesc(false); }
  };

  const removeFromQueue = async (artId) => {
    try {
      await axios.delete(`${API}/admin/podcast/queue/${artId}`, { headers });
      await loadQueue();
    } catch { toast.error("Failed to remove"); }
  };

  const scrapeAndQueue = async () => {
    const url = forceUrl.trim();
    if (!url) return;
    if (!url.startsWith("http")) { toast.error("Enter a valid URL starting with http(s)"); return; }
    setScrapingForce(true);
    try {
      const res = await axios.post(`${API}/admin/force-include-url`, { url, context: "podcast" }, { headers });
      setForceUrl("");
      await loadQueue();
      if (res.data.already_existed) {
        toast.success(`Already in feed — added to queue: "${res.data.article?.title?.slice(0, 55)}"`);
      } else {
        toast.success(`Scraped & queued: "${res.data.article?.title?.slice(0, 55)}"`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not scrape URL.");
    } finally {
      setScrapingForce(false);
    }
  };

  const searchArticles = useCallback(async (q) => {
    if (!q || q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const { data } = await axios.get(`${API}/admin/news?q=${encodeURIComponent(q)}&limit=8`, { headers });
      setSearchResults(data.articles || []);
    } catch { /* silent */ }
    finally { setSearching(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => searchArticles(articleSearch), 300);
    return () => clearTimeout(t);
  }, [articleSearch]);

  // ── Script Generation ─────────────────────────────────────────────────────
  const generateScript = async () => {
    setGeneratingScript(true);
    try {
      await axios.post(`${API}/admin/podcast/generate-script`, {}, { headers });
      toast.success("Script generation started — auto-refreshing…");
      startScriptPolling();
    } catch (e) {
      setGeneratingScript(false);
      toast.error(e.response?.data?.detail || "Generation failed");
    }
  };

  // ── Episode Actions ───────────────────────────────────────────────────────
  const approveEpisode = async (epId) => {
    setApproving(true);
    try {
      await axios.post(`${API}/admin/podcast/episodes/${epId}/approve`, { script: editedScript }, { headers });
      toast.success("Script approved — ready to record");
      await loadEpisodes();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
    finally { setApproving(false); }
  };

  const rejectEpisode = async (epId) => {
    setRejecting(true);
    try {
      await axios.post(`${API}/admin/podcast/episodes/${epId}/reject`, {}, { headers });
      toast.info("Script rejected");
      setActiveEpId(null);
      await loadEpisodes();
    } catch { toast.error("Reject failed"); }
    finally { setRejecting(false); }
  };

  const refineScript = async (epId) => {
    if (!refineInstruction.trim()) return;
    setRefining(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/podcast/episodes/${epId}/refine-script`,
        { instruction: refineInstruction, current_script: editedScript },
        { headers }
      );
      const refined = data.script || data.refined_script || "";
      setEditedScript(refined); setFullScript(refined); setRefineInstruction("");
      toast.success("Script refined");
    } catch { toast.error("Refinement failed"); }
    finally { setRefining(false); }
  };

  const generateAudio = async (epId) => {
    setRecordingEpId(epId);
    try {
      await axios.post(`${API}/admin/podcast/episodes/${epId}/generate-audio`, {}, { headers });
      toast.success("Recording started — polling for completion…");
      startEpisodePolling(["generating_audio"], (eps) => {
        setRecordingEpId(null);
        const ep = eps.find(e => e.id === epId);
        if (ep?.status === "audio_ready") toast.success("Audio ready! Listen before publishing.");
        else if (ep?.status === "audio_error") toast.error("Audio generation failed");
      });
      await loadEpisodes();
    } catch (e) {
      setRecordingEpId(null);
      toast.error(e.response?.data?.detail || "Record failed");
    }
  };

  const publishToSpreaker = async (epId) => {
    setPublishingEpId(epId);
    try {
      await axios.post(`${API}/admin/podcast/episodes/${epId}/publish-spreaker`, {}, { headers });
      toast.success("Publishing to Spreaker…");
      startEpisodePolling(["publishing"], async (eps) => {
        setPublishingEpId(null);
        const ep = eps.find(e => e.id === epId);
        if (ep?.status === "published") toast.success("Episode published!");
        else toast.error("Publish failed — check settings");
      });
      await loadEpisodes();
    } catch (e) {
      setPublishingEpId(null);
      toast.error(e.response?.data?.detail || "Publish failed");
    }
  };

  const deleteEpisode = async (epId) => {
    setDeletingEpId(epId);
    try {
      const { data } = await axios.delete(`${API}/admin/podcast/episodes/${epId}`, { headers });
      if (data.spreaker_warning) toast.warning(data.spreaker_warning);
      else toast.success("Episode deleted");
      setActiveEpId(null);
      setAudioUrl(null);
      await loadEpisodes();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setDeletingEpId(null);
    }
  };

  const syncSpreaker = async () => {
    setSyncingSpReaker(true);
    try {
      const { data } = await axios.post(`${API}/admin/podcast/sync-spreaker`, {}, { headers });
      if (data.removed > 0) {
        toast.success(`Sync complete — removed ${data.removed} episode${data.removed !== 1 ? "s" : ""} deleted on Spreaker`);
      } else {
        toast.success(`Sync complete — all ${data.checked} episode${data.checked !== 1 ? "s" : ""} still active on Spreaker`);
      }
      if (data.errors?.length) toast.warning(`${data.errors.length} error(s) during sync`);
      await loadEpisodes();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed — check Spreaker credentials");
    } finally {
      setSyncingSpReaker(false);
    }
  };

  const loadAudio = async (epId) => {    if (audioUrl) { setAudioUrl(null); return; }
    setLoadingAudio(true);
    try {
      const resp = await axios.get(
        `${API}/admin/podcast/episodes/${epId}/audio`,
        { headers, responseType: "blob" }
      );
      setAudioUrl(URL.createObjectURL(resp.data));
    } catch { toast.error("Could not load audio"); }
    finally { setLoadingAudio(false); }
  };

  // ── Settings ──────────────────────────────────────────────────────────────
  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      await axios.post(`${API}/admin/podcast/config`, configForm, { headers });
      toast.success("Settings saved");
      await loadConfig();
    } catch { toast.error("Save failed"); }
    finally { setSavingConfig(false); }
  };

  const saveCredentials = async () => {
    try {
      await axios.post(`${API}/admin/podcast/spreaker-save-credentials`, connectForm, { headers });
      toast.success("Spreaker credentials saved");
    } catch { toast.error("Failed to save credentials"); }
  };

  const startSpreakerOAuth = async () => {
    setConnecting(true);
    try {
      const { data } = await axios.get(`${API}/admin/podcast/spreaker-auth-url`, { headers });
      if (data.auth_url) window.open(data.auth_url, "_blank");
    } catch { toast.error("Failed to get Spreaker auth URL"); }
    finally { setConnecting(false); }
  };

  const uploadStyleSheet = async (file) => {
    if (!file) return;
    setUploadingStyleSheet(true);
    try {
      const content = await file.text();
      await axios.post(`${API}/admin/podcast/style-sheet`, { content, filename: file.name }, { headers });
      toast.success(`Style sheet "${file.name}" saved`);
      await loadConfig();
    } catch { toast.error("Failed to save style sheet"); }
    finally { setUploadingStyleSheet(false); }
  };

  // ── Push notification handlers ────────────────────────────────────────────
  const subscribePush = async () => {
    if (!("Notification" in window) || !("PushManager" in window)) { toast.error("Not supported"); return; }
    setPushLoading(true);
    try {
      const permission = await Notification.requestPermission();
      setPushPermission(permission);
      if (permission !== "granted") { toast.warning("Permission denied"); return; }
      const { data: keyData } = await axios.get(`${API}/admin/push/vapid-key`, { headers });
      const padding = "=".repeat((4 - (keyData.publicKey.length % 4)) % 4);
      const base64 = (keyData.publicKey + padding).replace(/-/g, "+").replace(/_/g, "/");
      const rawKey = Uint8Array.from(atob(base64), ch => ch.charCodeAt(0));
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: rawKey });
      await axios.post(`${API}/admin/push/subscribe`, { subscription: sub.toJSON() }, { headers });
      setPushSubscribed(true);
      setPushSubCount(n => n + 1);
      toast.success("Push notifications enabled");
    } catch (err) { toast.error(err.message || "Subscription failed"); }
    finally { setPushLoading(false); }
  };

  const unsubscribePush = async () => {
    setPushLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await axios.delete(`${API}/admin/push/subscribe`, { data: { endpoint: sub.endpoint }, headers });
        await sub.unsubscribe();
      }
      setPushSubscribed(false);
      setPushSubCount(n => Math.max(0, n - 1));
      toast.success("Push notifications disabled");
    } catch { toast.error("Failed"); }
    finally { setPushLoading(false); }
  };

  const sendTestPush = async () => {
    setPushLoading(true);
    try {
      await axios.post(`${API}/admin/push/test`, {}, { headers });
      toast.success("Test notification sent");
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
    finally { setPushLoading(false); }
  };

  // ── Derived data ──────────────────────────────────────────────────────────
  const epsByLane = LANES.reduce((acc, l) => { acc[l] = []; return acc; }, {});
  episodes.forEach(ep => {
    const col = STATUS_META[ep.status]?.col || "draft";
    if (epsByLane[col]) epsByLane[col].push(ep);
  });

  const activeEp = episodes.find(e => e.id === activeEpId) || null;
  const elModel = config.elevenlabs_model || "eleven_v3";

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <TabsContent value="podcast" className="mt-0">
      <div className={`min-h-screen p-6 space-y-6 ${dm ? "bg-[#141e1f]" : "bg-[#f9fafb]"}`}>
        {/* Tab navigation */}
        <div className="flex items-center gap-1 border-b border-transparent">
          <h2 className={`text-2xl font-semibold editorial-heading mr-4 ${c.text}`}>Podcast</h2>
          {[
            { id: "kanban",   label: "Pipeline" },
            { id: "settings", label: "Settings" },
            { id: "ttslab",   label: "TTS Lab" },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setMainTab(t.id)}
              data-testid={`podcast-main-tab-${t.id}`}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${mainTab === t.id
                ? (dm ? "bg-cyan-500/15 text-cyan-400" : "bg-[#006874]/10 text-[#006874]")
                : `${c.muted} hover:bg-white/5`
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {mainTab === "kanban" && (
          <PodcastKanbanBoard
            epsByLane={epsByLane}
            queue={queue}
            loadingEps={loadingEps}
            queueOpen={queueOpen}
            onQueueToggle={() => setQueueOpen(v => !v)}
            articleSearch={articleSearch}
            onArticleSearchChange={setArticleSearch}
            searchResults={searchResults}
            searching={searching}
            runningSelection={runningSelection}
            generatingScript={generatingScript}
            pollingScript={pollingScript}
            recordingEpId={recordingEpId}
            publishingEpId={publishingEpId}
            activeEpId={activeEpId}
            elModel={elModel}
            dm={dm}
            c={c}
            btnPrimary={btnPrimary}
            btnSecondary={btnSecondary}
            inputCls={inputCls}
            onRefresh={() => Promise.all([loadEpisodes(), loadQueue()])}
            onGenerateScript={generateScript}
            onAutoSelect={autoSelect}
            onAddToQueue={addToQueue}
            onRemoveFromQueue={removeFromQueue}
            onSelectEp={setActiveEpId}
            onGenerateAudio={generateAudio}
            onPublish={publishToSpreaker}
            onSyncSpreaker={syncSpreaker}
            syncingSpReaker={syncingSpReaker}
            forceUrl={forceUrl}
            onForceUrlChange={setForceUrl}
            onForceUrlAdd={scrapeAndQueue}
            scrapingForce={scrapingForce}
            detailPanel={activeEp ? (
              <PodcastDetailPanel
                activeEp={activeEp}
                dm={dm}
                c={c}
                btnPrimary={btnPrimary}
                btnSecondary={btnSecondary}
                inputCls={inputCls}
                labelCls={labelCls}
                editedScript={editedScript}
                onScriptChange={setEditedScript}
                loadingScript={loadingScript}
                refineInstruction={refineInstruction}
                onRefineChange={setRefineInstruction}
                refining={refining}
                approving={approving}
                rejecting={rejecting}
                recordingEpId={recordingEpId}
                publishingEpId={publishingEpId}
                pollingScript={pollingScript}
                elModel={elModel}
                audioUrl={audioUrl}
                loadingAudio={loadingAudio}
                editedTitle={editedTitle}
                onTitleChange={setEditedTitle}
                onTitleBlur={(newTitle) => saveTitle(activeEp.id, newTitle)}
                savingTitle={savingTitle}
                editedDescription={editedDescription}
                onDescChange={setEditedDescription}
                onDescBlur={(newDesc) => saveDescription(activeEp.id, newDesc)}
                savingDescription={savingDescription}
                generatingDesc={generatingDesc}
                onGenerateDesc={() => generateDescription(activeEp.id)}
                onClose={() => { setActiveEpId(null); setAudioUrl(null); }}
                onApprove={approveEpisode}
                onReject={rejectEpisode}
                onRefine={refineScript}
                onGenerateAudio={generateAudio}
                onPublish={publishToSpreaker}
                onLoadAudio={loadAudio}
                onDelete={deleteEpisode}
              />
            ) : null}
          />
        )}

        {mainTab === "ttslab" && (
          <PodcastTtsLabPanel episodes={episodes} config={config} headers={headers} dm={dm} c={c}
            btnPrimary={btnPrimary} btnSecondary={btnSecondary} inputCls={inputCls} labelCls={labelCls} />
        )}

        {mainTab === "settings" && (
          <PodcastSettingsPanel
            configForm={configForm}
            onConfigChange={(key, val) => setConfigForm(p => ({ ...p, [key]: val }))}
            config={config}
            connectForm={connectForm}
            onConnectChange={(key, val) => setConnectForm(p => ({ ...p, [key]: val }))}
            connecting={connecting}
            savingConfig={savingConfig}
            uploadingStyleSheet={uploadingStyleSheet}
            pushPermission={pushPermission}
            pushSubscribed={pushSubscribed}
            pushLoading={pushLoading}
            pushSubCount={pushSubCount}
            headers={headers}
            dm={dm}
            c={c}
            btnPrimary={btnPrimary}
            btnSecondary={btnSecondary}
            inputCls={inputCls}
            labelCls={labelCls}
            onSave={saveConfig}
            onSaveCredentials={saveCredentials}
            onSpreakerOAuth={startSpreakerOAuth}
            onUploadStyleSheet={uploadStyleSheet}
            onSubscribePush={subscribePush}
            onUnsubscribePush={unsubscribePush}
            onSendTestPush={sendTestPush}
          />
        )}
      </div>
    </TabsContent>
  );
}
